(() => {
  const init = () => document.querySelectorAll('[data-elune-gallery]').forEach(root => {
    if (root.dataset.galleryReady) return;
    const main = root.querySelector('[data-gallery-open]');
    const image = main?.querySelector('img');
    const dialog = root.querySelector('[data-zoom-dialog]');
    const zoom = dialog?.querySelector('[data-gallery-zoom-image]');
    const thumbnails = [...root.querySelectorAll('[data-gallery-source]')];
    if (!image || !dialog || !zoom || !thumbnails.length) return;
    root.dataset.galleryReady = 'true';
    let index = Math.max(0, thumbnails.findIndex(button => button.getAttribute('aria-pressed') === 'true'));
    let displayedIndex = index;
    let committedSrc = image.currentSrc || image.src;
    let selectionVersion = 0;
    let zoomVersion = 0;
    let opener;
    let previousOverflow;
    let idleHandle;
    let disposed = false;
    const counters = [...root.querySelectorAll('[data-gallery-count]')];
    const normalSizes = image.getAttribute('sizes') || '(max-width: 760px) 92vw, 648px';
    const normalWidths = [360, 540, 720, 960, 1200, 1600];
    const imageCache = new Map();
    const status = document.createElement('span');
    status.className = 'gallery-loading-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    status.style.cssText = 'position:absolute;left:14px;top:14px;z-index:3;padding:7px 10px;border-radius:3px;background:rgba(252,251,249,.94);color:#382b22;font-size:12px;line-height:1.4;pointer-events:none';
    main.parentElement.append(status);
    const zoomStatus = status.cloneNode(false);
    dialog.append(zoomStatus);
    const setStatus = (message = '', busy = false) => {
      main.setAttribute('aria-busy', String(busy));
      dialog.setAttribute('aria-busy', String(busy));
      [status, zoomStatus].forEach(label => {
        label.textContent = message;
        label.hidden = !message;
      });
    };
    const urlAtWidth = (source, width) => {
      const url = new URL(source, location.href);
      url.searchParams.set('width', width);
      return url.href;
    };
    const normalRequest = target => {
      const source = thumbnails[target].dataset.gallerySource;
      const renderedWidth = image.getBoundingClientRect().width || main.clientWidth || Math.min(window.innerWidth * .92, 648);
      const requestedWidth = Math.ceil(renderedWidth * (window.devicePixelRatio || 1));
      const width = normalWidths.find(value => value >= requestedWidth) || normalWidths[normalWidths.length - 1];
      return {
        src: urlAtWidth(source, width),
        srcset: normalWidths.map(value => `${urlAtWidth(source, value)} ${value}w`).join(', '),
        sizes: normalSizes,
        key: `${source}|normal|${window.innerWidth}|${window.devicePixelRatio || 1}`
      };
    };
    // A decoded image stays in this small cache; only adjacent normal-size images are warmed.
    const loadImage = (request, priority = 'high') => {
      const cached = imageCache.get(request.key);
      if (cached) {
        if (priority === 'high') cached.image.fetchPriority = 'high';
        return cached.promise;
      }
      const pendingImage = new Image();
      const entry = { image: pendingImage };
      const promise = new Promise((resolve, reject) => {
        let settled = false;
        const finish = error => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          pendingImage.onload = pendingImage.onerror = null;
          if (error) {
            if (imageCache.get(request.key) === entry) imageCache.delete(request.key);
            reject(error);
          } else resolve(pendingImage);
        };
        const timer = setTimeout(() => finish(new Error('Image loading timed out.')), 20000);
        pendingImage.onload = async () => {
          try { if (pendingImage.decode) await pendingImage.decode(); }
          catch (error) { if (!pendingImage.naturalWidth) return finish(error); }
          finish();
        };
        pendingImage.onerror = () => finish(new Error('Image could not be loaded.'));
        pendingImage.decoding = 'async';
        pendingImage.fetchPriority = priority;
        if (request.sizes) pendingImage.sizes = request.sizes;
        if (request.srcset) pendingImage.srcset = request.srcset;
        pendingImage.src = request.src;
      });
      entry.promise = promise;
      imageCache.set(request.key, entry);
      if (imageCache.size > 12) imageCache.delete(imageCache.keys().next().value);
      return promise;
    };
    const cancelWarmup = () => {
      if (idleHandle === undefined) return;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
      else clearTimeout(idleHandle);
      idleHandle = undefined;
    };
    const warmNeighbors = () => {
      cancelWarmup();
      if (thumbnails.length < 2 || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const current = displayedIndex;
      const warm = () => {
        idleHandle = undefined;
        if (disposed || current !== displayedIndex || index !== displayedIndex) return;
        const neighbors = new Set([(current + 1) % thumbnails.length, (current + thumbnails.length - 1) % thumbnails.length]);
        neighbors.forEach(target => loadImage(normalRequest(target), 'low').catch(() => {}));
      };
      idleHandle = window.requestIdleCallback ? window.requestIdleCallback(warm, { timeout: 1500 }) : setTimeout(warm, 300);
    };
    const updateZoom = () => {
      const target = displayedIndex;
      const version = ++zoomVersion;
      // Show the already-loaded normal image immediately, then sharpen only the open zoom.
      zoom.src = committedSrc;
      zoom.alt = thumbnails[target].getAttribute('aria-label') || image.alt;
      const src = new URL(thumbnails[target].dataset.gallerySource, location.href).href;
      loadImage({ src, key: src + '|zoom' }).then(() => {
        if (!disposed && dialog.open && version === zoomVersion && target === displayedIndex) zoom.src = src;
      }).catch(() => {});
    };
    const select = async next => {
      const target = (next + thumbnails.length) % thumbnails.length;
      index = target;
      const version = ++selectionVersion;
      ++zoomVersion;
      cancelWarmup();
      const selected = thumbnails[target];
      const rail = selected.parentElement;
      const itemRect = selected.getBoundingClientRect();
      const railRect = rail.getBoundingClientRect();
      const offset = itemRect.left < railRect.left ? itemRect.left - railRect.left : itemRect.right > railRect.right ? itemRect.right - railRect.right : 0;
      if (offset) rail.scrollBy({ left: offset, behavior: 'auto' });
      if (target === displayedIndex) {
        setStatus();
        if (dialog.open) updateZoom();
        warmNeighbors();
        return;
      }
      setStatus('Loading image…', true);
      const request = normalRequest(target);
      try {
        const decodedImage = await loadImage(request);
        if (disposed || version !== selectionVersion) return;
        committedSrc = decodedImage.currentSrc || request.src;
        image.sizes = request.sizes;
        image.srcset = request.srcset;
        image.src = request.src;
        image.alt = selected.getAttribute('aria-label') || image.alt;
        displayedIndex = target;
        thumbnails.forEach(button => button.setAttribute('aria-pressed', String(button === selected)));
        counters.forEach(counter => counter.textContent = `${target + 1} / ${thumbnails.length}`);
        setStatus();
        if (dialog.open) updateZoom();
        warmNeighbors();
      } catch (error) {
        if (disposed || version !== selectionVersion) return;
        index = displayedIndex;
        setStatus('Image unavailable. Please try again.');
      }
    };
    const open = event => {
      if (dialog.open) return;
      opener = event.currentTarget;
      previousOverflow = document.documentElement.style.overflow;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      updateZoom();
    };
    let skipMainClick = false;
    const enableSwipe = surface => {
      let start;
      surface.addEventListener('pointerdown', event => {
        if (surface === main) skipMainClick = false;
        if (event.pointerType !== 'touch' || !event.isPrimary || thumbnails.length < 2) { start = null; return; }
        start = { id: event.pointerId, x: event.clientX, y: event.clientY };
      });
      surface.addEventListener('pointerup', event => {
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        start = null;
        if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy) * 1.3) return;
        if (surface === main) skipMainClick = true;
        select(index + (dx < 0 ? 1 : -1));
      });
      surface.addEventListener('pointercancel', () => { start = null; });
    };
    enableSwipe(main);
    enableSwipe(dialog.querySelector('.gallery-zoom-stage'));
    main.addEventListener('click', event => {
      const skip = skipMainClick && event.detail > 0;
      skipMainClick = false;
      if (skip) { event.preventDefault(); return; }
      open(event);
    });
    const bindAction = (button, action) => {
      let touchStart;
      let skipClick = false;
      let handledTouch = false;
      button.addEventListener('pointerdown', event => {
        skipClick = false;
        handledTouch = false;
        touchStart = event.pointerType === 'touch' && event.isPrimary
          ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
      });
      button.addEventListener('pointercancel', () => { touchStart = null; });
      button.addEventListener('pointerup', event => {
        if (!touchStart || touchStart.id !== event.pointerId) return;
        const distance = Math.hypot(event.clientX - touchStart.x, event.clientY - touchStart.y);
        touchStart = null;
        skipClick = true;
        if (distance <= 12) { handledTouch = true; action(event); }
      });
      button.addEventListener('touchend', event => {
        if (handledTouch && event.cancelable) event.preventDefault();
        handledTouch = false;
      }, { passive: false });
      button.addEventListener('click', event => {
        const skip = skipClick && event.detail > 0;
        skipClick = false;
        if (skip) { event.preventDefault(); return; }
        action(event);
      });
    };
    bindAction(root.querySelector('[data-gallery-expand]'), open);
    root.querySelectorAll('[data-gallery-prev]').forEach(button => bindAction(button, () => select(index - 1)));
    root.querySelectorAll('[data-gallery-next]').forEach(button => bindAction(button, () => select(index + 1)));
    thumbnails.forEach((button, next) => button.addEventListener('click', () => select(next)));
    bindAction(root.querySelector('[data-gallery-close]'), () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => {
      ++zoomVersion;
      document.documentElement.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    root.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const thumbnail = event.target.closest('[data-gallery-source]');
      let next;
      if (event.key === 'ArrowRight') next = index + 1;
      if (event.key === 'ArrowLeft') next = index - 1;
      if (thumbnail && event.key === 'Home') next = 0;
      if (thumbnail && event.key === 'End') next = thumbnails.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      select(next);
      if (thumbnail) thumbnails[index].focus({ preventScroll: true });
    });
    root.querySelectorAll('[data-gallery-prev],[data-gallery-next],[data-gallery-count]').forEach(control => { control.hidden = thumbnails.length < 2; });
    counters.forEach(counter => counter.textContent = `${index + 1} / ${thumbnails.length}`);
    if (image.complete && image.naturalWidth) warmNeighbors();
    else image.addEventListener('load', warmNeighbors, { once: true });
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      disposed = true;
      ++selectionVersion;
      ++zoomVersion;
      cancelWarmup();
      if (dialog.open) dialog.close();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
